// Shared types, pure derivation logic, facet constants, and the client-side
// filter/sort/paginate pipeline for the admin Users tab. Everything here is
// framework-agnostic and side-effect free so the orchestrator can derive view
// models once and run the pipeline synchronously on every keystroke.

import type { Profile, UserRole } from "@/types/admin";
import { buildFeaturePermissions, featureKeys, type FeatureKey } from "@/lib/authz/policy";
import {
  PLAN_QUOTAS,
  PLAN_TIER_RANK,
  deriveCurrentPlan,
  type PlanTier,
} from "@/lib/authz/plans";

export type { FeatureKey } from "@/lib/authz/policy";
export type { PlanTier } from "@/lib/authz/plans";

// ---------------------------------------------------------------------------
// Labels & display constants
// ---------------------------------------------------------------------------

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  paintbooth: "Paint Booth",
  eupviewer: "EUP Viewer",
  addoneupcreator: "Addon EUP Creator",
  weaponviewer: "Weapon Designer",
  uvstudio: "UV Studio",
  liverymagic: "Livery Magic",
  itemimage: "Asset Art",
  badgemaker: "Badge Maker",
  embroiderytool: "Embroidery Tool",
  blinklab: "Blinklab",
  blinklab3d: "3D BlinkLab",
  metaroom: "Easy Meta",
  packbench: "PackBench",
  codesmith: "Code Smith",
  codevisualizer: "Code Visualizer",
  scriptdocs: "Script Docs",
  tripoai: "ModelForge",
  chevronbuilder: "Chevron Builder",
  creatorconvert: "Creator Convert",
  inklab: "Ink Lab",
  handlingdiff: "Handling Diff",
  resmon: "Resmon Report",
  assets: "Asset Directory",
  modkitviewer: "Modkit Viewer",
};

export const TOOL_FEATURES: FeatureKey[] = [...featureKeys];

// Tools each paid plan unlocks, for the admin "Give plan" shortcut. Mirrors the
// V2 entries of FEATURES_BY_TITLE in src/lib/server/sealSubscriptions.ts — that
// module reads server env so it can't be imported here; the parity test in
// src/test/entitlementBundles.test.ts fails if the two drift.
const VEHICLE_DEVELOPER_TOOLS: FeatureKey[] = [
  "paintbooth",
  "liverymagic",
  "uvstudio",
  "blinklab",
  "metaroom",
  "chevronbuilder",
  "creatorconvert",
  "handlingdiff",
  "assets",
  "blinklab3d",
  "modkitviewer",
];
const SERVER_DEVELOPER_TOOLS: FeatureKey[] = [
  "packbench",
  "codesmith",
  "codevisualizer",
  "scriptdocs",
  "eupviewer",
  "addoneupcreator",
  "weaponviewer",
  "itemimage",
  "embroiderytool",
  "tripoai",
  "metaroom",
  "creatorconvert",
  "resmon",
  "assets",
];
const ALL_TOOLS: FeatureKey[] = [
  ...new Set<FeatureKey>([...VEHICLE_DEVELOPER_TOOLS, ...SERVER_DEVELOPER_TOOLS, "tripoai"]),
];

export const ADMIN_PLAN_BUNDLES: Record<Exclude<PlanTier, "free">, FeatureKey[]> = {
  paintbooth: ["paintbooth"],
  vehicle_dev: VEHICLE_DEVELOPER_TOOLS,
  server_dev: SERVER_DEVELOPER_TOOLS,
  all_services: ALL_TOOLS,
  // Studio early access (studioEarlyAccessFeatureKeys in @/lib/authz/policy).
  studio: [...ALL_TOOLS, "inklab"],
};

export const PLAN_TIER_ORDER: PlanTier[] = (
  Object.keys(PLAN_TIER_RANK) as PlanTier[]
).sort((a, b) => PLAN_TIER_RANK[a] - PLAN_TIER_RANK[b]);

// Badge tint per tier — free is muted, paid tiers escalate toward the primary
// accent so plan strength reads at a glance.
export const TIER_TINT: Record<PlanTier, string> = {
  free: "border-border/60 bg-muted/40 text-muted-foreground",
  paintbooth: "border-sky-500/30 bg-sky-500/15 text-sky-400",
  vehicle_dev: "border-teal-500/30 bg-teal-500/15 text-teal-400",
  server_dev: "border-indigo-500/30 bg-indigo-500/15 text-indigo-400",
  all_services: "border-primary/30 bg-primary/15 text-primary",
  studio: "border-accent/40 bg-accent/15 text-accent-foreground",
};

// ---------------------------------------------------------------------------
// IP / country helpers
// ---------------------------------------------------------------------------

export const PRIVATE_IP_RE = /^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.|127\.|::1$|localhost)/i;

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const IPV6_RE = /^(([0-9a-f]{1,4}:){7}[0-9a-f]{1,4}|([0-9a-f]{1,4}:){1,7}:|([0-9a-f]{1,4}:){1,6}:[0-9a-f]{1,4}|([0-9a-f]{1,4}:){1,5}(:[0-9a-f]{1,4}){1,2}|([0-9a-f]{1,4}:){1,4}(:[0-9a-f]{1,4}){1,3}|([0-9a-f]{1,4}:){1,3}(:[0-9a-f]{1,4}){1,4}|([0-9a-f]{1,4}:){1,2}(:[0-9a-f]{1,4}){1,5}|[0-9a-f]{1,4}:((:[0-9a-f]{1,4}){1,6})|:((:[0-9a-f]{1,4}){1,7}|:))$/i;

export function normalizeIp(input: string): string {
  const trimmed = (input ?? "").trim();
  if (!trimmed) return "";
  return trimmed.startsWith("::ffff:") ? trimmed.slice(7) : trimmed;
}

export function isValidIp(input: string): boolean {
  const ip = normalizeIp(input);
  if (!ip) return false;
  return IPV4_RE.test(ip) || IPV6_RE.test(ip);
}

export function isPrivateIp(input: string): boolean {
  return PRIVATE_IP_RE.test(normalizeIp(input));
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

export function codeToCountryName(code: string): string {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

export function countryFlagUrl(code: string): string {
  return `https://flagcdn.com/w20/${code.toLowerCase()}.png`;
}

export function countryFlagSrcSet(code: string): string {
  return `${countryFlagUrl(code)} 1x, https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[unit]}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  return new Date(ms).toLocaleString();
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Compact relative age, e.g. "now", "5m", "3h", "6d", "4mo", "2y".
export function relativeTime(ms: number | null, now = Date.now()): string {
  if (ms == null) return "never";
  const diff = Math.max(0, now - ms);
  if (diff < MINUTE) return "now";
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h`;
  if (diff < 30 * DAY) return `${Math.floor(diff / DAY)}d`;
  if (diff < 365 * DAY) return `${Math.floor(diff / (30 * DAY))}mo`;
  return `${Math.floor(diff / (365 * DAY))}y`;
}

export function getInitials(name: string | null, email: string | null): string {
  if (name) {
    return name
      .split(" ")
      .map((w) => w[0])
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return (email?.[0] ?? "?").toUpperCase();
}

// ---------------------------------------------------------------------------
// Entitlement / storage derivation (pure)
// ---------------------------------------------------------------------------

export function getStoredPersonalFeatures(profile: Profile): FeatureKey[] {
  const raw = profile.personal_subscription_data;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];

  const matchedSubscriptions = (raw as { matchedSubscriptions?: unknown }).matchedSubscriptions;
  if (!Array.isArray(matchedSubscriptions)) return [];

  const features = new Set<FeatureKey>();
  for (const entry of matchedSubscriptions) {
    if (!entry || typeof entry !== "object") continue;
    if ((entry as { accessActive?: unknown }).accessActive !== true) continue;
    const matchedFeatures = (entry as { matchedFeatures?: unknown }).matchedFeatures;
    if (!Array.isArray(matchedFeatures)) continue;
    for (const feature of matchedFeatures) {
      if (featureKeys.includes(feature as FeatureKey)) features.add(feature as FeatureKey);
    }
  }
  return Array.from(features);
}

export function getEffectiveServices(profile: Profile): FeatureKey[] {
  const personalFeatures = getStoredPersonalFeatures(profile);
  const permissions = buildFeaturePermissions(profile as unknown as Record<string, unknown>, {
    isAdmin: profile.role === "admin",
    personalFeatures,
  });
  return featureKeys.filter((feature) => permissions[feature]);
}

export type StorageLevel = "none" | "ok" | "warning" | "critical";

export type StorageQuota = {
  used: number;
  cap: number;
  tier: PlanTier;
  pct: number;
  level: StorageLevel;
  overCap: boolean;
};

export function getUserStorageQuota(profile: Profile): StorageQuota {
  const used = Math.max(0, Number(profile.storage_used_bytes ?? 0) || 0);
  const tier =
    profile.role === "admin" ? "studio" : deriveCurrentPlan(profile.personal_subscription_data ?? null);
  const cap = PLAN_QUOTAS[tier].storageBytes;
  const rawPct = cap > 0 ? (used / cap) * 100 : 0;
  const pct = Math.min(100, Math.round(rawPct));
  const overCap = cap > 0 && used > cap;
  const level: StorageLevel =
    cap === 0 ? "none" : overCap || pct >= 95 ? "critical" : pct >= 80 ? "warning" : "ok";
  return { used, cap, tier, pct, level, overCap };
}

// ---------------------------------------------------------------------------
// Row view-model
// ---------------------------------------------------------------------------

export type UserFlags = {
  autoBlocked: boolean;
  bypass: boolean;
  dynamic: boolean;
  extraIps: boolean;
  note: boolean;
};

export type UserRow = {
  profile: Profile;
  id: string;
  name: string;
  email: string;
  role: UserRole;
  planTier: PlanTier;
  planLabel: string;
  planRank: number;
  services: FeatureKey[];
  serviceCount: number;
  storage: StorageQuota;
  tokens: number;
  tokensSpent: number;
  flags: UserFlags;
  riskScore: number;
  lastActiveMs: number | null;
  createdMs: number;
  discordLinked: boolean;
  /** Normalized last_login_ip; "" when absent. Used for country lookups. */
  ip: string;
  searchBlob: string;
};

export function buildUserRow(profile: Profile): UserRow {
  const services = getEffectiveServices(profile);
  const storage = getUserStorageQuota(profile);
  const tier = profile.role === "admin" ? "studio" : deriveCurrentPlan(profile.personal_subscription_data ?? null);
  const extraIps = Array.isArray(profile.extra_allowed_ips) ? profile.extra_allowed_ips : [];
  const note = typeof profile.admin_note === "string" && profile.admin_note.trim().length > 0;

  const flags: UserFlags = {
    autoBlocked: Boolean(profile.ip_auto_blocked),
    bypass: Boolean(profile.ip_lock_disabled),
    dynamic: Boolean(profile.dynamic_ip_mode),
    extraIps: extraIps.length > 0,
    note,
  };

  // "Needs attention" weighting — auto-blocked dominates so the worst accounts
  // float to the top under the default risk-desc sort.
  const riskScore =
    (flags.autoBlocked ? 100 : 0) +
    (flags.bypass ? 10 : 0) +
    (flags.note ? 5 : 0) +
    (flags.dynamic ? 1 : 0) +
    (flags.extraIps ? 1 : 0);

  const lastActiveMs = profile.last_sign_in_at ? Date.parse(profile.last_sign_in_at) : NaN;
  const createdMs = profile.created_at ? Date.parse(profile.created_at) : NaN;

  const searchBlob = [
    profile.email,
    profile.full_name,
    profile.discord_user_id,
    profile.discord_username,
    profile.id,
  ]
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join(" ")
    .toLowerCase();

  return {
    profile,
    id: profile.id,
    name: profile.full_name ?? "",
    email: profile.email ?? "",
    role: profile.role,
    planTier: tier,
    planLabel: PLAN_QUOTAS[tier].label,
    planRank: PLAN_TIER_RANK[tier],
    services,
    serviceCount: services.length,
    storage,
    tokens: Number(profile.tokens ?? 0) || 0,
    tokensSpent: Number(profile.tokens_lifetime_spent ?? 0) || 0,
    flags,
    riskScore,
    lastActiveMs: Number.isNaN(lastActiveMs) ? null : lastActiveMs,
    createdMs: Number.isNaN(createdMs) ? 0 : createdMs,
    discordLinked: Boolean(profile.discord_user_id),
    ip: normalizeIp(String(profile.last_login_ip ?? "")),
    searchBlob,
  };
}

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

export type SortKey =
  | "risk"
  | "name"
  | "email"
  | "role"
  | "plan"
  | "tools"
  | "tokens"
  | "storage"
  | "lastActive"
  | "created";

export type RoleFilter = "all" | "admin" | "user";
export type DiscordFilter = "all" | "linked" | "unlinked";
export type SecurityFlag = keyof UserFlags;
export type ActivityFilter = "any" | "7d" | "30d" | "90d" | "dormant" | "never";
/** Special tokens live alongside FeatureKeys in the tools facet. */
export const TOOLS_ANY = "__any__";
export const TOOLS_NONE = "__none__";

export type Density = "comfortable" | "compact";

export type FilterState = {
  search: string;
  role: RoleFilter;
  discord: DiscordFilter;
  plans: PlanTier[];
  tools: string[]; // TOOLS_ANY | TOOLS_NONE | FeatureKey ; OR semantics
  flags: SecurityFlag[];
  storage: StorageLevel[];
  tokenMin: number | null;
  tokenMax: number | null;
  activity: ActivityFilter;
  sortKey: SortKey;
  sortAsc: boolean;
  page: number;
  pageSize: number;
  density: Density;
};

export const DEFAULT_FILTER_STATE: FilterState = {
  search: "",
  role: "all",
  discord: "all",
  plans: [],
  tools: [],
  flags: [],
  storage: [],
  tokenMin: null,
  tokenMax: null,
  activity: "any",
  sortKey: "risk",
  sortAsc: false,
  page: 1,
  pageSize: 100,
  density: "compact",
};

export type SetFilterState = (patch: Partial<FilterState>) => void;

// Keys that, when changed, must reset pagination back to page 1.
export const FILTER_RESET_KEYS: (keyof FilterState)[] = [
  "search",
  "role",
  "discord",
  "plans",
  "tools",
  "flags",
  "storage",
  "tokenMin",
  "tokenMax",
  "activity",
  "sortKey",
  "sortAsc",
  "pageSize",
];

/** State that should NOT reset paging and is persisted across sessions. */
export function countActiveFilters(state: FilterState): number {
  let n = 0;
  if (state.search.trim()) n += 1;
  if (state.role !== "all") n += 1;
  if (state.discord !== "all") n += 1;
  if (state.plans.length) n += 1;
  if (state.tools.length) n += 1;
  if (state.flags.length) n += 1;
  if (state.storage.length) n += 1;
  if (state.tokenMin != null || state.tokenMax != null) n += 1;
  if (state.activity !== "any") n += 1;
  return n;
}

export function clearFiltersPatch(): Partial<FilterState> {
  return {
    search: "",
    role: "all",
    discord: "all",
    plans: [],
    tools: [],
    flags: [],
    storage: [],
    tokenMin: null,
    tokenMax: null,
    activity: "any",
    page: 1,
  };
}

// ---------------------------------------------------------------------------
// Facet option metadata
// ---------------------------------------------------------------------------

export const SECURITY_FLAG_DEFS: { key: SecurityFlag; label: string }[] = [
  { key: "autoBlocked", label: "Auto-blocked" },
  { key: "bypass", label: "IP lock bypass" },
  { key: "dynamic", label: "Dynamic IP" },
  { key: "extraIps", label: "Has extra IPs" },
  { key: "note", label: "Has admin note" },
];

export const STORAGE_LEVEL_DEFS: { key: StorageLevel; label: string }[] = [
  { key: "critical", label: "Critical (≥95% / over)" },
  { key: "warning", label: "Warning (≥80%)" },
  { key: "ok", label: "OK" },
  { key: "none", label: "No storage plan" },
];

export const ACTIVITY_OPTIONS: { value: ActivityFilter; label: string }[] = [
  { value: "any", label: "Any time" },
  { value: "7d", label: "Active ≤ 7d" },
  { value: "30d", label: "Active ≤ 30d" },
  { value: "90d", label: "Active ≤ 90d" },
  { value: "dormant", label: "Dormant > 90d" },
  { value: "never", label: "Never signed in" },
];

export function activityMatches(lastActiveMs: number | null, filter: ActivityFilter, now: number): boolean {
  if (filter === "any") return true;
  if (filter === "never") return lastActiveMs == null;
  if (lastActiveMs == null) return false;
  const age = now - lastActiveMs;
  switch (filter) {
    case "7d":
      return age <= 7 * DAY;
    case "30d":
      return age <= 30 * DAY;
    case "90d":
      return age <= 90 * DAY;
    case "dormant":
      return age > 90 * DAY;
    default:
      return true;
  }
}

export type FacetCounts = {
  role: { admin: number; user: number };
  discord: { linked: number; unlinked: number };
  plans: Record<PlanTier, number>;
  tools: Record<string, number>; // includes TOOLS_ANY / TOOLS_NONE / per-feature
  flags: Record<SecurityFlag, number>;
  storage: Record<StorageLevel, number>;
  active7d: number;
  storagePressured: number; // warning + critical
};

// Counts each facet option across the FULL row set (not the filtered subset) so
// admins can see how many users a filter would surface before applying it.
export function computeFacetCounts(rows: UserRow[]): FacetCounts {
  const now = Date.now();
  const plans = Object.fromEntries(PLAN_TIER_ORDER.map((t) => [t, 0])) as Record<PlanTier, number>;
  const tools: Record<string, number> = { [TOOLS_ANY]: 0, [TOOLS_NONE]: 0 };
  for (const f of TOOL_FEATURES) tools[f] = 0;
  const flags: Record<SecurityFlag, number> = {
    autoBlocked: 0,
    bypass: 0,
    dynamic: 0,
    extraIps: 0,
    note: 0,
  };
  const storage: Record<StorageLevel, number> = { none: 0, ok: 0, warning: 0, critical: 0 };
  let admin = 0;
  let linked = 0;
  let active7d = 0;

  for (const r of rows) {
    if (r.role === "admin") admin += 1;
    if (r.discordLinked) linked += 1;
    plans[r.planTier] += 1;
    if (r.serviceCount > 0) tools[TOOLS_ANY] += 1;
    else tools[TOOLS_NONE] += 1;
    for (const f of r.services) tools[f] += 1;
    if (r.flags.autoBlocked) flags.autoBlocked += 1;
    if (r.flags.bypass) flags.bypass += 1;
    if (r.flags.dynamic) flags.dynamic += 1;
    if (r.flags.extraIps) flags.extraIps += 1;
    if (r.flags.note) flags.note += 1;
    storage[r.storage.level] += 1;
    if (activityMatches(r.lastActiveMs, "7d", now)) active7d += 1;
  }

  return {
    role: { admin, user: rows.length - admin },
    discord: { linked, unlinked: rows.length - linked },
    plans,
    tools,
    flags,
    storage,
    active7d,
    storagePressured: storage.warning + storage.critical,
  };
}

// ---------------------------------------------------------------------------
// Pipeline: search → filter → sort → paginate (pure, synchronous)
// ---------------------------------------------------------------------------

export type PipelineResult = {
  filtered: UserRow[];
  pageRows: UserRow[];
  total: number;
  filteredCount: number;
  pageCount: number;
  currentPage: number;
  pageStart: number;
};

function cmp(a: number | string, b: number | string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function makeComparator(key: SortKey, asc: boolean) {
  const dir = asc ? 1 : -1;
  return (a: UserRow, b: UserRow): number => {
    let r = 0;
    switch (key) {
      case "risk":
        r = cmp(a.riskScore, b.riskScore);
        break;
      case "name":
        // Empty names sort to the end regardless of direction.
        r = cmp(a.name.toLowerCase() || "￿", b.name.toLowerCase() || "￿") || cmp(a.email, b.email);
        break;
      case "email":
        r = cmp(a.email.toLowerCase(), b.email.toLowerCase());
        break;
      case "role":
        r = cmp(a.role, b.role);
        break;
      case "plan":
        r = cmp(a.planRank, b.planRank);
        break;
      case "tools":
        r = cmp(a.serviceCount, b.serviceCount);
        break;
      case "tokens":
        r = cmp(a.tokens, b.tokens);
        break;
      case "storage": {
        // "No storage plan" (cap 0) always sorts last.
        const aNone = a.storage.cap === 0;
        const bNone = b.storage.cap === 0;
        if (aNone !== bNone) return aNone ? 1 : -1;
        r = cmp(a.storage.pct, b.storage.pct) || cmp(a.storage.used, b.storage.used);
        break;
      }
      case "lastActive": {
        if (a.lastActiveMs == null && b.lastActiveMs == null) r = 0;
        else if (a.lastActiveMs == null) return 1; // nulls last
        else if (b.lastActiveMs == null) return -1;
        else r = cmp(a.lastActiveMs, b.lastActiveMs);
        break;
      }
      case "created":
        r = cmp(a.createdMs, b.createdMs);
        break;
      default:
        r = cmp(a.riskScore, b.riskScore);
    }
    if (r !== 0) return r * dir;
    return b.createdMs - a.createdMs; // stable tie-break: newest first
  };
}

export function runPipeline(rows: UserRow[], state: FilterState): PipelineResult {
  const now = Date.now();
  let filtered = rows;

  const q = state.search.trim().toLowerCase();
  if (q) filtered = filtered.filter((r) => r.searchBlob.includes(q));

  if (state.role !== "all") filtered = filtered.filter((r) => r.role === state.role);

  if (state.discord === "linked") filtered = filtered.filter((r) => r.discordLinked);
  else if (state.discord === "unlinked") filtered = filtered.filter((r) => !r.discordLinked);

  if (state.plans.length) {
    const set = new Set(state.plans);
    filtered = filtered.filter((r) => set.has(r.planTier));
  }

  if (state.tools.length) {
    const anyTok = state.tools.includes(TOOLS_ANY);
    const noneTok = state.tools.includes(TOOLS_NONE);
    const specific = state.tools.filter((t) => t !== TOOLS_ANY && t !== TOOLS_NONE) as FeatureKey[];
    filtered = filtered.filter((r) => {
      if (anyTok && r.serviceCount > 0) return true;
      if (noneTok && r.serviceCount === 0) return true;
      if (specific.length && specific.some((t) => r.services.includes(t))) return true;
      return false;
    });
  }

  if (state.flags.length) {
    filtered = filtered.filter((r) => state.flags.some((f) => r.flags[f]));
  }

  if (state.storage.length) {
    const set = new Set(state.storage);
    filtered = filtered.filter((r) => set.has(r.storage.level));
  }

  if (state.tokenMin != null) {
    const min = state.tokenMin;
    filtered = filtered.filter((r) => r.tokens >= min);
  }
  if (state.tokenMax != null) {
    const max = state.tokenMax;
    filtered = filtered.filter((r) => r.tokens <= max);
  }

  if (state.activity !== "any") {
    filtered = filtered.filter((r) => activityMatches(r.lastActiveMs, state.activity, now));
  }

  const sorted = filtered === rows ? [...rows] : filtered;
  sorted.sort(makeComparator(state.sortKey, state.sortAsc));

  const total = rows.length;
  const filteredCount = sorted.length;
  const pageCount = Math.max(1, Math.ceil(filteredCount / state.pageSize));
  const currentPage = Math.min(Math.max(1, state.page), pageCount);
  const pageStart = (currentPage - 1) * state.pageSize;
  const pageRows = sorted.slice(pageStart, pageStart + state.pageSize);

  return { filtered: sorted, pageRows, total, filteredCount, pageCount, currentPage, pageStart };
}

// ---------------------------------------------------------------------------
// Component prop contracts (pinned — implemented by sibling files)
// ---------------------------------------------------------------------------

export interface UsersSummaryStripProps {
  rows: UserRow[];
  counts: FacetCounts;
  state: FilterState;
  onApply: (patch: Partial<FilterState>) => void;
}

export interface UsersFilterBarProps {
  state: FilterState;
  setState: SetFilterState;
  counts: FacetCounts;
  filteredCount: number;
  total: number;
  isFetching: boolean;
  onRefresh: () => void;
}

export interface UsersTableProps {
  pageRows: UserRow[];
  state: FilterState;
  setState: SetFilterState;
  isLoading: boolean;
  currentUserId: string | null;
  onOpen: (row: UserRow) => void;
  onResetIpTimer: (row: UserRow) => void;
  onDelete: (row: UserRow) => void;
}

export interface UserDetailSheetProps {
  row: UserRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId: string | null;
  canStepPrev: boolean;
  canStepNext: boolean;
  onStep: (dir: -1 | 1) => void;
  onRequestDelete: (row: UserRow) => void;
}
